import { RefCallback, useCallback, useEffect, useRef } from 'react';

import { Row, Table } from '@tanstack/react-table';

interface UseGenerateSelectionStateArgs<TItem> {
  table: Table<TItem>;
  itemsCount: number;
}

interface UseGenerateSelectionStateReturn<TItem> {
  selectedRowObjects: Row<TItem>[];
  selectedRowsCount: number;
  hasTableRows: boolean;
  /**
   * Attach to the element that wraps the table. The hook uses it to find the
   * row-selection checkboxes that exist when data first arrives, and pulses
   * those — and only those — exactly once via the Web Animations API.
   */
  pulseContainerRef: RefCallback<HTMLDivElement>;
  clearSelection: () => void;
}

// 1s × 2 iterations = 2s total, per product spec.
const PULSE_KEYFRAMES: Keyframe[] = [
  { boxShadow: '0 0 0 0 hsl(var(--primary) / 0.7)' },
  { boxShadow: '0 0 0 6px hsl(var(--primary) / 0)' },
  { boxShadow: '0 0 0 0 hsl(var(--primary) / 0.7)' },
];
const PULSE_OPTIONS: KeyframeAnimationOptions = {
  duration: 1_000,
  iterations: 2,
  easing: 'ease-out',
};

/**
 * Derives selection-related facts for the generate pages and runs the one-off
 * checkbox-pulse hint.
 *
 * Why imperative (Web Animations API) instead of a CSS rule keyed on a data
 * attribute: a CSS rule re-fires the animation whenever a new matching
 * checkbox element mounts (expanding a row, filter changes, "add custom
 * line", etc.). We want it to play exactly once, for the checkboxes that
 * exist when the table first renders with data — and never again for this
 * mount. Calling `.animate()` on the captured elements snapshots them; any
 * checkboxes mounted later are not affected.
 */
export const useGenerateSelectionState = <TItem>({
  table,
  itemsCount,
}: UseGenerateSelectionStateArgs<TItem>): UseGenerateSelectionStateReturn<TItem> => {
  const selectedRowObjects = table.getSelectedRowModel().flatRows;
  const selectedRowsCount = selectedRowObjects.length;
  const hasTableRows = itemsCount > 0;

  const hasPlayedRef = useRef(false);
  const containerElRef = useRef<HTMLDivElement | null>(null);

  const pulseContainerRef = useCallback<RefCallback<HTMLDivElement>>(node => {
    containerElRef.current = node;
  }, []);

  useEffect(() => {
    if (hasPlayedRef.current) return;
    if (!hasTableRows) return;
    const container = containerElRef.current;
    if (!container) return;

    // Defer to next frame so the table cells have actually rendered the
    // checkboxes after `data` settles.
    const rafId = window.requestAnimationFrame(() => {
      if (hasPlayedRef.current) return;
      const checkboxes = container.querySelectorAll<HTMLElement>('button[role="checkbox"]');
      if (checkboxes.length === 0) return;
      hasPlayedRef.current = true;
      checkboxes.forEach(el => {
        // Match the checkbox's visual border-radius so the pulsing box-shadow
        // doesn't bleed past its rounded corners while the animation runs.
        const originalBorderRadius = el.style.borderRadius;
        el.style.borderRadius = '0.125rem';
        const animation = el.animate(PULSE_KEYFRAMES, PULSE_OPTIONS);
        const restoreBorderRadius = () => {
          el.style.borderRadius = originalBorderRadius;
        };
        animation.onfinish = restoreBorderRadius;
        animation.oncancel = restoreBorderRadius;
      });
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [hasTableRows]);

  return {
    selectedRowObjects,
    selectedRowsCount,
    hasTableRows,
    pulseContainerRef,
    clearSelection: () => table.resetRowSelection(),
  };
};
